import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import { useHelmChartValues } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartValues';

import {
  parseConnectionFields,
  ConnectionField,
} from '../InstallView/parseConnectionFields';
import { InstallFormValues } from '../InstallView/types';

import { toFieldStrings } from './addonOverrides';

interface AddonUpgradeState {
  /** Parsed fields of the form's target version's values.yaml (the new version when upgrading, or the installed version when just editing values). */
  fields: Array<ConnectionField>;
  /** True when the form's target version differs from the installed one. */
  isUpgrade: boolean;
  /** True when the target version differs from the installed one, or any field differs from the release. */
  changed: boolean;
  /** True once the target version's values.yaml has resolved. */
  fieldsReady: boolean;
}

/**
 * Derives the upgrade form's version/dirty state from the current Formik values.
 * Compares the installed release against the edited form as strings over the
 * chart's field set, so typed release values don't read as dirty against the
 * stringy inputs. Falls back to the target version's chart defaults as the
 * baseline when the release values couldn't be read, matching how
 * ConfigurationStep seeds the form. Must be called inside the install form's
 * <Formik>.
 */
export function useAddonUpgradeState({
  chartSource,
  installedVersion,
  releaseValues,
}: {
  chartSource: string;
  installedVersion: string;
  releaseValues?: Record<string, unknown>;
}): AddonUpgradeState {
  const { values } = useFormikContext<InstallFormValues>();
  const { version } = values;

  const valuesQuery = useHelmChartValues({
    chart: version ? chartSource : '',
    version,
  });

  const fields = useMemo(
    () =>
      valuesQuery.data?.values
        ? parseConnectionFields(valuesQuery.data.values)
        : [],
    [valuesQuery.data]
  );

  const baselineFields = useMemo(
    () =>
      releaseValues
        ? toFieldStrings(fields, releaseValues)
        : Object.fromEntries(fields.map((field) => [field.key, field.value])),
    [fields, releaseValues]
  );

  const isUpgrade = version !== installedVersion;
  const changed =
    isUpgrade ||
    !isEqual(toFieldStrings(fields, values.values), baselineFields);

  return { fields, isUpgrade, changed, fieldsReady: valuesQuery.isSuccess };
}
